import * as React from "react";
import {
  List,
  Datagrid,
  Show,
  SelectInput,
  DateInput,
  TopToolbar,
  ExportButton,
  FilterButton,
  FunctionField,
  useRecordContext,
  useNotify,
  useRefresh,
} from "react-admin";
import CancelIcon from "@mui/icons-material/Cancel";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogContentText,
  DialogActions, TextField as MuiTextField, CircularProgress,
  Card, CardContent, Chip, Stack, Typography, alpha,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";
import EuroIcon from "@mui/icons-material/EuroOutlined";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import TimelineIcon from "@mui/icons-material/TimelineOutlined";
import NotesIcon from "@mui/icons-material/NotesOutlined";
import BoltIcon from "@mui/icons-material/BoltOutlined";
import { PersonChip, Section, StatusChip, TechInfo, CopyButton, KV, KVGrid, ShowLink, collectTechnical } from "../../components/ui";
import { fmtDateTime, fmtTime, fmtMoney, fmtCents, toDate, isTechnicalKey } from "../../utils/format";
import { EnumChip } from "../../components/EnumChip";
import { InfoGrid } from "../../components/InfoGrid";
import { ConfirmActionButton } from "../../components/ConfirmActionButton";
import {
  BOOKING_STATUS, BOOKING_TYPE, BOOKING_TERMINAL_STATUSES, PAYMENT_STATUS, isBookingCancelled, choicesFromEnum,
} from "../../enums";
import { httpClient } from "../../api/httpClient";
import { API_BASE_URL } from "../../api/config";

// Le backend (GET /bookings) attend dateFrom / dateTo en epoch ms (Long,
// filtre sur la date de création du RDV). Le DateInput fournit "YYYY-MM-DD" :
// conversion dans le dataProvider (resourceMap.bookings.transformFilter),
// début / fin de journée heure de Paris.
// Pas de filtre patient / pro : GET /bookings ne le supporte pas — les RDV
// d'un patient ou d'un pro sont visibles depuis sa fiche (onglet Rendez-vous).
const filters = [
  <SelectInput key="status" source="status" label="Statut" choices={choicesFromEnum(BOOKING_STATUS)} alwaysOn />,
  <SelectInput key="bookingType" source="bookingType" label="Mode" choices={choicesFromEnum(BOOKING_TYPE)} />,
  <DateInput key="dateFrom" source="dateFrom" label="Créé du" />,
  <DateInput key="dateTo" source="dateTo" label="Créé au" />,
];

const ListActions = () => (
  <TopToolbar>
    <FilterButton />
    <ExportButton />
  </TopToolbar>
);

const SpecialityCell = ({ r }) =>
  r?.nameSpeciality ? (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: r.colorSpeciality || "primary.main", flexShrink: 0 }} />
      <Typography variant="body2" noWrap>{r.nameSpeciality}</Typography>
    </Stack>
  ) : (
    "—"
  );

const MoneyText = ({ v }) => (
  <Typography variant="body2" component="span" sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
    {fmtMoney(v)}
  </Typography>
);

export const BookingList = () => (
  <List
    filters={filters}
    actions={<ListActions />}
    sort={{ field: "createdDate", order: "DESC" }}
    perPage={25}
  >
    <Datagrid rowClick="show" bulkActionButtons={false}>
      <FunctionField
        label="Date du RDV"
        sortBy="slot.startTime"
        render={(r) => (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>{fmtDateTime(r?.slot?.startTime)}</Typography>
            <Typography variant="caption" color="text.secondary">{r?.slot?.duration ? `${r.slot.duration} min` : ""}</Typography>
          </Box>
        )}
      />
      <FunctionField
        label="Patient"
        sortable={false}
        render={(r) => <PersonChip id={r?.patientId} firstName={r?.firstNamePatient} lastName={r?.lastNamePatient} resource="patients" dense />}
      />
      <FunctionField
        label="Professionnel"
        sortable={false}
        render={(r) => <PersonChip id={r?.professionalId || r?.proId} firstName={r?.firstNamePro} lastName={r?.lastNamePro} resource="professionals" dense />}
      />
      <FunctionField label="Spécialité" sortBy="nameSpeciality" render={(r) => <SpecialityCell r={r} />} />
      <EnumChip source="bookingType" label="Mode" enumObj={BOOKING_TYPE} />
      <EnumChip source="status" label="Statut" enumObj={BOOKING_STATUS} />
      <FunctionField label="Prix" sortBy="price" textAlign="right" render={(r) => <MoneyText v={r?.price} />} />
    </Datagrid>
  </List>
);

// ── Actions admin sur un RDV ───────────────────────────────────────────────
// Contrats backend (BookingController / PaymentController) :
//  - annulation      : DELETE /bookings/{id}?actorType=PROFESSIONAL|PATIENT
//                      (PROFESSIONAL → CANCELED_BY_PRO, aucun frais patient ;
//                       toute autre valeur → CANCELED_BY_USER, frais tardifs
//                       30 % < 24 h). Il n'existe pas d'acteur "ADMIN".
//  - no-show         : PUT /bookings/noshow/{id}
//  - frais no-show   : PUT /bookings/noshow/{id}/validate-fee?validate=true|false
//                      (query param, défaut true : un body est ignoré)
//  - remboursement   : POST /payment/refund/{paymentIntentId}?amount=<centimes>
//                      (PI retrouvé via GET /payment?bookingId=…)
//  - reprogrammation : POST /bookings/{id}/reschedule?newSlotId=…&actorType=PROFESSIONAL
//  - prix fixe       : POST /bookings/{id}/propose-fixed-price, body = nombre brut
//  - codes de séance : POST /bookings/{id}/validate-start|validate-end, body { code }

const fmtSlotStart = (r) => fmtDateTime(r?.slot?.startTime, "date inconnue");

const fmtEuro = (v) => fmtMoney(v);

const CancelButton = () => {
  const r = useRecordContext();
  if (!r || BOOKING_TERMINAL_STATUSES.includes(r.status)) return null;
  const cancel = (actorType) =>
    httpClient(
      `${API_BASE_URL}/bookings/${encodeURIComponent(r.id)}?actorType=${actorType}`,
      { method: "DELETE" }
    );
  return (
    <>
      <ConfirmActionButton
        label="Annuler (sans frais)"
        icon={<CancelIcon />}
        color="error"
        confirmTitle="Annuler ce rendez-vous sans frais"
        confirmText={`Annuler le RDV du ${fmtSlotStart(r)} ? L'annulation est enregistrée côté praticien : aucun frais n'est prélevé au patient, l'empreinte est libérée et le créneau rendu disponible.`}
        successMessage="Rendez-vous annulé (sans frais)"
        onConfirm={() => cancel("PROFESSIONAL")}
      />
      <ConfirmActionButton
        label="Annuler (frais patient)"
        variant="outlined"
        color="error"
        confirmTitle="Annuler au nom du patient"
        confirmText={`Annuler le RDV du ${fmtSlotStart(r)} au nom du patient ? Si le RDV est dans moins de 24 h, les frais d'annulation tardive (30 %) seront prélevés.`}
        successMessage="Rendez-vous annulé (côté patient)"
        onConfirm={() => cancel("PATIENT")}
      />
    </>
  );
};

const NoShowButton = () => {
  const r = useRecordContext();
  if (!r || BOOKING_TERMINAL_STATUSES.includes(r.status)) return null;
  return (
    <ConfirmActionButton
      label="Non présenté"
      icon={<EventBusyIcon />}
      color="warning"
      confirmTitle="Marquer comme non présenté"
      confirmText={`Marquer le patient absent au RDV du ${fmtSlotStart(r)} ? Des frais de no-show (30 %) seront mis en attente de validation.`}
      successMessage="RDV marqué non présenté"
      onConfirm={() =>
        httpClient(`${API_BASE_URL}/bookings/noshow/${encodeURIComponent(r.id)}`, { method: "PUT" })
      }
    />
  );
};

// Remboursement : le PaymentIntent n'est pas exposé dans le BookingDTO, on le
// retrouve via la ligne Payment liée au RDV. Montant saisi en euros, envoyé
// en centimes (paramètre `amount` obligatoire côté backend).
const RefundButton = () => {
  const r = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [open, setOpen] = React.useState(false);
  const [payment, setPayment] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [amount, setAmount] = React.useState("");
  if (!r) return null;

  const openDialog = async () => {
    setOpen(true);
    setPayment(null);
    setLoading(true);
    try {
      const { json } = await httpClient(
        `${API_BASE_URL}/payment?bookingId=${encodeURIComponent(r.id)}`
      );
      const list = Array.isArray(json) ? json : [];
      const p = list[0] || null;
      setPayment(p || { missing: true });
      const cents = r?.paymentInformation?.capturedAmount ?? p?.amount;
      setAmount(cents != null ? (Number(cents) / 100).toFixed(2) : "");
    } catch (e) {
      setPayment({ error: e.message });
    } finally {
      setLoading(false);
    }
  };

  const submit = async () => {
    const cents = Math.round(Number(String(amount).replace(",", ".")) * 100);
    if (!payment?.paymentIntent || !cents || cents < 1) return;
    setLoading(true);
    try {
      await httpClient(
        `${API_BASE_URL}/payment/refund/${encodeURIComponent(payment.paymentIntent)}?amount=${cents}`,
        { method: "POST" }
      );
      notify(`Remboursement de ${(cents / 100).toFixed(2)} € déclenché`, { type: "success" });
      setOpen(false);
      refresh();
    } catch (e) {
      notify(`Remboursement impossible : ${e.message}`, { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button size="small" variant="contained" color="info" onClick={openDialog}>
        Rembourser
      </Button>
      <Dialog open={open} onClose={() => !loading && setOpen(false)}>
        <DialogTitle>Rembourser ce RDV</DialogTitle>
        <DialogContent>
          {loading && !payment ? (
            <CircularProgress size={20} />
          ) : payment?.error ? (
            <DialogContentText color="error">Erreur : {payment.error}</DialogContentText>
          ) : payment?.missing || !payment?.paymentIntent ? (
            <DialogContentText>
              Aucun paiement Stripe n'est rattaché à ce RDV (forfait, points ou paiement non
              finalisé) : rien à rembourser ici.
            </DialogContentText>
          ) : (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                RDV du {fmtSlotStart(r)}
                {payment.amount != null ? ` — montant initial ${fmtCents(payment.amount)}` : ""}.
              </DialogContentText>
              <MuiTextField
                label="Montant à rembourser (€)"
                type="number"
                size="small"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                inputProps={{ min: 0.01, step: 0.01 }}
                fullWidth
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={loading}>Fermer</Button>
          <Button
            onClick={submit}
            color="info"
            variant="contained"
            disabled={loading || !payment?.paymentIntent || !(Number(amount) > 0)}
          >
            {loading && payment ? <CircularProgress size={20} /> : "Rembourser"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const RescheduleButton = () => {
  const r = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [slotId, setSlotId] = React.useState("");
  if (!r || BOOKING_TERMINAL_STATUSES.includes(r.status)) return null;
  const reschedule = async () => {
    if (!slotId.trim()) return;
    try {
      // actorType=PROFESSIONAL : l'admin agit pour le compte du praticien
      // (pas de règle des 4 h côté patient).
      await httpClient(
        `${API_BASE_URL}/bookings/${encodeURIComponent(r.id)}/reschedule?newSlotId=${encodeURIComponent(slotId.trim())}&actorType=PROFESSIONAL`,
        { method: "POST" }
      );
      notify("Rendez-vous reprogrammé", { type: "success" });
      setSlotId("");
      refresh();
    } catch (e) {
      notify(`Reprogrammation impossible : ${e.message}`, { type: "error" });
    }
  };
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <MuiTextField size="small" label="Identifiant du nouveau créneau"
        value={slotId} onChange={(e) => setSlotId(e.target.value)} sx={{ minWidth: 240 }} />
      <Button variant="outlined" onClick={reschedule} disabled={!slotId.trim()}>
        Reprogrammer
      </Button>
    </Stack>
  );
};

// validate-start / validate-end exigent le code de séance dans le body
// ({ code }) : on réutilise ceux générés pour le RDV.
const ValidateSession = () => {
  const r = useRecordContext();
  if (!r || BOOKING_TERMINAL_STATUSES.includes(r.status)) return null;
  const post = (step, code) =>
    httpClient(`${API_BASE_URL}/bookings/${encodeURIComponent(r.id)}/${step}`, {
      method: "POST",
      body: JSON.stringify({ code }),
    });
  return (
    <Box sx={{ display: "flex", gap: 1 }}>
      <ConfirmActionButton label="Valider début" color="primary"
        disabled={!r.startValidationCode}
        confirmTitle="Valider début de session"
        confirmText={`Valider le début de la séance du ${fmtSlotStart(r)} ?`}
        successMessage="Début validé"
        onConfirm={() => post("validate-start", r.startValidationCode)}
      />
      <ConfirmActionButton label="Valider fin" color="success"
        disabled={!r.endValidationCode}
        confirmTitle="Valider fin de session"
        confirmText={`Valider la fin de la séance du ${fmtSlotStart(r)} ?`}
        successMessage="Fin validée"
        onConfirm={() => post("validate-end", r.endValidationCode)}
      />
    </Box>
  );
};

const ValidateNoShowFee = () => {
  const r = useRecordContext();
  if (!r || r.status !== "NO_SHOW") return null;
  if (r.noShowFeeStatus && r.noShowFeeStatus !== "PENDING") return null;
  const fee = r.noShowFeeAmount != null ? ` (${fmtEuro(r.noShowFeeAmount)})` : "";
  const call = (validate) =>
    httpClient(
      `${API_BASE_URL}/bookings/noshow/${encodeURIComponent(r.id)}/validate-fee?validate=${validate}`,
      { method: "PUT" }
    );
  return (
    <Box sx={{ display: "flex", gap: 1 }}>
      <ConfirmActionButton label="Accepter frais 30%" color="success"
        confirmTitle="Accepter frais no-show"
        confirmText={`Confirmer le prélèvement des frais de no-show${fee} pour le RDV du ${fmtSlotStart(r)} ?`}
        successMessage="Frais validés"
        onConfirm={() => call(true)}
      />
      <ConfirmActionButton label="Refuser frais" color="warning"
        confirmTitle="Refuser frais no-show"
        confirmText={`Renoncer aux frais de no-show${fee} : aucun prélèvement ne sera effectué.`}
        successMessage="Frais annulés"
        onConfirm={() => call(false)}
      />
    </Box>
  );
};

const ProposeFixedPrice = () => {
  const r = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [price, setPrice] = React.useState("");
  if (!r || BOOKING_TERMINAL_STATUSES.includes(r.status)) return null;
  const propose = async () => {
    const value = Number(String(price).replace(",", "."));
    if (!value || value <= 0) return;
    if (!window.confirm(`Proposer un prix fixe de ${fmtEuro(value)} (prix actuel : ${fmtEuro(r.price)}) ?`)) return;
    try {
      // Le backend lit un Double brut (@RequestBody Double), pas un objet.
      await httpClient(`${API_BASE_URL}/bookings/${encodeURIComponent(r.id)}/propose-fixed-price`, {
        method: "POST",
        body: JSON.stringify(value),
      });
      notify("Prix fixe proposé", { type: "success" });
      setPrice("");
      refresh();
    } catch (e) {
      notify(`Proposition impossible : ${e.message}`, { type: "error" });
    }
  };
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <MuiTextField size="small" type="number" label="Prix (€)"
        value={price} onChange={(e) => setPrice(e.target.value)} sx={{ width: 120 }} />
      <Button variant="outlined" onClick={propose} disabled={!price}>
        Proposer un prix fixe
      </Button>
    </Stack>
  );
};

// Paiement Stripe rattaché au RDV (GET /payment?bookingId=…) : statut,
// date d'encaissement / de versement, remboursement éventuel.
const useBookingPayment = (id) => {
  const [state, setState] = React.useState({ loading: true, payment: null });
  React.useEffect(() => {
    if (!id) return undefined;
    let alive = true;
    httpClient(`${API_BASE_URL}/payment?bookingId=${encodeURIComponent(id)}`)
      .then(({ json }) => alive && setState({ loading: false, payment: (Array.isArray(json) ? json : json?.content || [])[0] || null }))
      .catch(() => alive && setState({ loading: false, payment: null }));
    return () => {
      alive = false;
    };
  }, [id]);
  return state;
};

const durationOf = (r) => {
  const start = r?.slot?.startTime;
  const end = r?.slot?.endTime;
  return r?.slot?.duration || (start && end ? Math.round((end - start) / 60000) : null);
};

const dayPart = (t, opts) =>
  t ? new Intl.DateTimeFormat("fr-FR", { ...opts, timeZone: "Europe/Paris" }).format(new Date(Number(t))) : "";

const BookingHeader = () => {
  const r = useRecordContext();
  if (!r) return null;
  const dur = durationOf(r);
  const start = r?.slot?.startTime;
  return (
    <Card sx={{ mb: 2 }}>
      <CardContent sx={{ p: { xs: 2, md: 3 } }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={(t) => ({
                width: 64,
                height: 64,
                borderRadius: 3,
                bgcolor: alpha(t.palette.primary.main, 0.1),
                color: "primary.dark",
                display: "grid",
                placeItems: "center",
                textAlign: "center",
                flexShrink: 0,
              })}
            >
              <Box>
                <Typography sx={{ fontFamily: "Sora, sans-serif", fontWeight: 700, fontSize: 22, lineHeight: 1 }}>
                  {dayPart(start, { day: "numeric" }) || "?"}
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 600, textTransform: "uppercase" }}>
                  {dayPart(start, { month: "short" })}
                </Typography>
              </Box>
            </Box>
            <Box>
              <Typography variant="h5" component="h1">
                {fmtDateTime(start)}
                {r?.slot?.endTime ? ` – ${fmtTime(r.slot.endTime)}` : ""}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {r.nameSpeciality || "Séance"}
                {dur ? ` · ${dur} min` : ""}
                {r.reason ? ` · ${r.reason}` : ""}
              </Typography>
              <Stack direction="row" spacing={0.75} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                <StatusChip value={r.status} enumObj={BOOKING_STATUS} />
                <StatusChip value={r.bookingType} enumObj={BOOKING_TYPE} />
                {r.usePoints && <Chip size="small" label="Points Ura utilisés" />}
                {r.forfaitId && <Chip size="small" label="Séance de forfait" />}
              </Stack>
            </Box>
          </Stack>
          <Box sx={{ textAlign: { md: "right" } }}>
            <Typography variant="caption" color="text.secondary">Prix de la séance</Typography>
            <Typography sx={{ fontFamily: "Sora, sans-serif", fontWeight: 700, fontSize: 26 }}>{fmtMoney(r.fixedPrice ?? r.price)}</Typography>
            {r.fixedPrice != null && r.price != null && r.fixedPrice !== r.price && (
              <Typography variant="caption" color="text.secondary">prix initial {fmtMoney(r.price)}</Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

const ACTOR = { PATIENT: "Patient", PROFESSIONAL: "Professionnel", ADMIN: "Admin" };

const PeopleCards = () => {
  const r = useRecordContext();
  if (!r) return null;
  return (
    <>
      <Section title="Patient" icon={<PersonOutlineIcon />} action={r.patientId ? <ShowLink resource="patients" id={r.patientId}>Fiche</ShowLink> : null}>
        <PersonChip id={r.patientId} firstName={r.firstNamePatient} lastName={r.lastNamePatient} email={r.emailPatient} photo={r.urlPhoto} resource="patients" />
        <Box sx={{ mt: 2 }}>
          <KVGrid min={140}>
            <KV label="Téléphone" value={r.phonePatient} />
            <KV label="Pour un proche" value={r.procheId ? "Oui" : "Non"} />
            {r.invitationEmailSent != null && <KV label="Invitation envoyée" value={r.invitationEmailSent ? "Oui" : "Non"} />}
            {r.invitationEmailError && <KV label="Erreur d'invitation" value={r.invitationEmailError} />}
          </KVGrid>
        </Box>
      </Section>
      <Section title="Professionnel" icon={<MedicalServicesOutlinedIcon />} action={r.professionalId ? <ShowLink resource="professionals" id={r.professionalId}>Fiche</ShowLink> : null}>
        <PersonChip id={r.professionalId} firstName={r.firstNamePro} lastName={r.lastNamePro} resource="professionals" />
        <Box sx={{ mt: 2 }}>
          <KVGrid min={140}>
            <KV label="Spécialité">
              {r.nameSpeciality ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: r.colorSpeciality || "primary.main" }} />
                  <Typography variant="body2">{r.nameSpeciality}</Typography>
                </Stack>
              ) : null}
            </KV>
            <KV label="Dernière action par" value={r.lastActionBy ? ACTOR[r.lastActionBy] || r.lastActionBy : null} />
          </KVGrid>
        </Box>
      </Section>
    </>
  );
};

const NO_SHOW_FEE = { PENDING: "en attente de validation", VALIDATED: "validés", ACCEPTED: "acceptés", REJECTED: "refusés", WAIVED: "abandonnés" };

const paymentChip = (v) => <StatusChip value={v} enumObj={PAYMENT_STATUS} />;

const AmountRow = ({ label, value, strong, hint }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2} sx={{ py: 0.9, borderBottom: 1, borderColor: "divider", "&:last-of-type": { borderBottom: 0 } }}>
    <Box>
      <Typography variant="body2" color={strong ? "text.primary" : "text.secondary"} sx={{ fontWeight: strong ? 600 : 400 }}>{label}</Typography>
      {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
    </Box>
    <Typography variant="body2" sx={{ fontWeight: strong ? 700 : 500, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{value}</Typography>
  </Stack>
);

const AmountsCard = () => {
  const r = useRecordContext();
  const { payment } = useBookingPayment(r?.id);
  if (!r) return null;
  const pi = r.paymentInformation || {};
  const refunded = payment?.paymentStatusEnum === "REFUNDED";
  const status = pi.paymentStatus || payment?.paymentStatusEnum;
  return (
    <Section title="Montants" icon={<EuroIcon />} action={status ? paymentChip(status) : null}>
      <AmountRow label="Prix de la séance" value={fmtMoney(r.price)} strong />
      {r.fixedPrice != null && <AmountRow label="Prix fixe proposé" value={fmtMoney(r.fixedPrice)} />}
      <AmountRow label="Empreinte bancaire" hint="Montant autorisé à la réservation" value={fmtCents(pi.authorizedAmount)} />
      <AmountRow label="Capturé" hint="Montant effectivement encaissé" value={fmtCents(pi.capturedAmount)} />
      <AmountRow
        label="Remboursé"
        hint={
          refunded || pi.paymentStatus === "REFUNDED"
            ? "Paiement remboursé (montant du paiement Stripe)"
            : pi.paymentStatus === "PARTIALLY_REFUNDED"
              ? "Remboursement partiel (montant exact non exposé par l'API)"
              : "Aucun remboursement enregistré"
        }
        value={refunded || pi.paymentStatus === "REFUNDED" ? fmtCents(payment?.amount ?? pi.capturedAmount) : pi.paymentStatus === "PARTIALLY_REFUNDED" ? "partiel" : fmtMoney(0)}
      />
      {pi.grossAmount != null && <AmountRow label="Montant brut" hint="Base de calcul des frais" value={fmtCents(pi.grossAmount)} />}
      <AmountRow label="Frais plateforme" value={fmtCents(pi.platformFee)} />
      <AmountRow label="Net praticien" value={fmtCents(pi.ownerNet)} />
      {r.noShowFeeAmount != null && (
        <AmountRow
          label="Frais de non-présentation"
          hint={r.noShowFeeStatus ? `Statut : ${NO_SHOW_FEE[r.noShowFeeStatus] || r.noShowFeeStatus}` : null}
          value={fmtMoney(r.noShowFeeAmount)}
        />
      )}
      {pi.minutesActual != null && <AmountRow label="Minutes réelles" value={`${pi.minutesActual} min`} />}
    </Section>
  );
};

const CodeBox = ({ label, value }) => (
  <Box sx={(t) => ({ p: 1.5, borderRadius: 3, bgcolor: t.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "#F5F7F5", textAlign: "center" })}>
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{label}</Typography>
    <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
      <Typography sx={{ fontFamily: "ui-monospace, Menlo, monospace", fontSize: 20, fontWeight: 700, letterSpacing: "0.12em" }}>
        {value || "—"}
      </Typography>
      <CopyButton value={value} />
    </Stack>
  </Box>
);

const CodesCard = () => {
  const r = useRecordContext();
  if (!r) return null;
  return (
    <Section title="Codes de séance" icon={<KeyOutlinedIcon />}>
      <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))" }}>
        <CodeBox label="Validation" value={r.validationCode} />
        <CodeBox label="Début" value={r.startValidationCode} />
        <CodeBox label="Fin" value={r.endValidationCode} />
      </Box>
    </Section>
  );
};

const TimelineCard = () => {
  const r = useRecordContext();
  const { payment } = useBookingPayment(r?.id);
  if (!r) return null;
  const cancelled = isBookingCancelled(r.status);
  const events = [
    { at: r.createdDate, label: "Rendez-vous créé" },
    { at: r?.paymentInformation?.paymentDate, label: "Empreinte bancaire" },
    { at: payment?.createdAt, label: "Paiement enregistré" },
    { at: r?.slot?.startTime, label: "Début prévu" },
    { at: r.videoStartedAt, label: "Visio démarrée" },
    { at: r.actualStartTime, label: "Début réel" },
    { at: r.actualEndTime, label: "Fin réelle" },
    { at: r.videoEndedAt, label: "Visio terminée", hint: r.videoDurationSeconds ? `${Math.round(Number(r.videoDurationSeconds) / 60)} min de vidéo` : null },
    { at: r?.slot?.endTime, label: "Fin prévue" },
    { at: payment?.paidAt, label: "Paiement encaissé" },
    { at: payment?.nextPayoutDate, label: "Versement au praticien prévu" },
    {
      at: r.modifiedDate,
      label: cancelled ? "Annulation" : "Dernière modification",
      hint: cancelled ? r.reasonCancellation : null,
      color: cancelled ? "error.main" : null,
    },
  ]
    .filter((e) => toDate(e.at))
    .sort((a, b) => toDate(a.at) - toDate(b.at));
  return (
    <Section title="Chronologie" icon={<TimelineIcon />}>
      {events.length === 0 ? (
        <Typography variant="body2" color="text.secondary">Aucun évènement daté</Typography>
      ) : (
        <Box sx={{ position: "relative", pl: 2.5 }}>
          <Box sx={{ position: "absolute", left: 5, top: 6, bottom: 6, width: 2, bgcolor: "divider", borderRadius: 1 }} />
          {events.map((e, i) => {
            const future = toDate(e.at).getTime() > Date.now();
            return (
              <Box key={i} sx={{ position: "relative", pb: i === events.length - 1 ? 0 : 1.75 }}>
                <Box
                  sx={{
                    position: "absolute",
                    left: -20.5,
                    top: 5,
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    bgcolor: e.color || (future ? "background.paper" : "primary.main"),
                    border: 2,
                    borderColor: e.color || "primary.main",
                  }}
                />
                <Typography variant="body2" sx={{ fontWeight: 600, color: future ? "text.secondary" : "text.primary" }}>{e.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {fmtDateTime(e.at)}
                  {e.hint ? ` · ${e.hint}` : ""}
                  {future ? " · à venir" : ""}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </Section>
  );
};

const DetailsCard = () => (
  <Section title="Détails" icon={<NotesIcon />}>
    <InfoGrid
      fields={[
        { label: "Motif", source: "reason" },
        { label: "Notes", source: "notes" },
        { label: "Motif d'annulation", source: "reasonCancellation" },
        { label: "Rappel anticipé", source: "alertEarlier", type: "bool" },
        { label: "Points Ura utilisés", source: "usePoints", type: "bool" },
        { label: "Séance de forfait", source: (r) => !!r?.forfaitId, type: "bool" },
        { label: "Documents joints", source: (r) => (Array.isArray(r?.documents) ? r.documents.length : 0) },
      ]}
    />
  </Section>
);

const ActionsCard = () => (
  <Section title="Actions" icon={<BoltIcon />}>
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <CancelButton />
        <NoShowButton />
        <RefundButton />
      </Stack>
      <ValidateSession />
      <ValidateNoShowFee />
      <RescheduleButton />
      <ProposeFixedPrice />
    </Stack>
  </Section>
);

const PreConsultationCard = () => {
  const r = useRecordContext();
  const f = r?.preConsultationForm;
  if (!f || typeof f !== "object") return null;
  const entries = Object.entries(f).filter(([k, v]) => v != null && v !== "" && typeof v !== "object" && !isTechnicalKey(k));
  if (!entries.length) return null;
  return (
    <Section title="Pré-consultation" icon={<NotesIcon />}>
      <KVGrid>
        {entries.map(([k, v]) => (
          <KV key={k} label={k} value={typeof v === "boolean" ? (v ? "Oui" : "Non") : String(v)} />
        ))}
      </KVGrid>
    </Section>
  );
};

// IDs du RDV + ceux du paiement Stripe lié (PaymentIntent, client, compte…).
const BookingTech = () => {
  const r = useRecordContext();
  const { payment } = useBookingPayment(r?.id);
  if (!r) return null;
  const items = [
    ...collectTechnical(r),
    ...collectTechnical(payment || {}).map((x) => ({ ...x, key: `paiement.${x.key}`, label: `Paiement · ${x.key}` })),
  ];
  return <TechInfo items={items} />;
};

export const BookingShow = () => (
  <Show component="div" actions={false}>
    <BookingHeader />
    <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.5fr) minmax(0, 1fr)" }, alignItems: "start" }}>
      <Stack spacing={2}>
        <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" } }}>
          <PeopleCards />
        </Box>
        <AmountsCard />
        <DetailsCard />
        <PreConsultationCard />
        <ActionsCard />
      </Stack>
      <Stack spacing={2}>
        <TimelineCard />
        <CodesCard />
      </Stack>
    </Box>
    <BookingTech />
  </Show>
);

// Pas d'édition / création génériques : PUT /bookings/{id} et POST /bookings
// attendent un BookingDTO complet rattaché à un créneau (slot) — les actions
// métier ci-dessus (annulation, reprogrammation, no-show…) couvrent les
// modifications admin.
export default {
  list: BookingList,
  show: BookingShow,
};
